// Display rules of the Nordic lookups usage page (ENG2-2465, T11 of ENG2-2452).

import numeral from 'numeral'
import { NordicLookupSource } from 'services/cm-api/get-nordic-lookup-windows'

export const formatNumber = (value: number): string =>
  numeral(value).format('0,0')

// The sorts RisikaAPI accepts (ENG2-2462); anything else is a 400.
export const SORT_OPTIONS = [
  { value: '-percent', label: 'Closest to cap' },
  { value: '-used', label: 'Most used' },
  { value: '-cap', label: 'Largest cap' },
  { value: 'name', label: 'Name' },
] as const

export type NordicUsageSort = (typeof SORT_OPTIONS)[number]['value']

export const DEFAULT_SORT: NordicUsageSort = '-percent'

// A 2026-package customer whose cap is still 2,147,483,647 is not metered at
// all, so the number is not a budget; say so instead of printing it.
export const capLabel = ({
  cap,
  unlimited,
}: {
  cap: number | null
  unlimited: boolean
}): string => {
  if (unlimited) return 'Unlimited'
  if (cap === null) return '—'
  return formatNumber(cap)
}

// The period resets soon: worth a call before the anniversary.
export const ENDS_SOON_DAYS = 30

export const endsSoon = (daysLeft: number | null): boolean =>
  daysLeft !== null && daysLeft < ENDS_SOON_DAYS

export const daysLeftLabel = (daysLeft: number | null): string => {
  if (daysLeft === null) return ''
  if (daysLeft === 0) return 'Resets today'
  return `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`
}

const SOURCE_LABELS: Record<NordicLookupSource, string> = {
  live: 'Looked up',
  monitoring: 'Monitored',
  backfill: 'From history',
}

export const sourceLabel = (source: NordicLookupSource): string =>
  SOURCE_LABELS[source] ?? source

// Companies carried over from monitoring at the start of a contract year are not
// requests: RisikaAPI dates them when they were added to monitoring, with no
// route and 0 requests until the customer actually opens them (2026-10-05).
export const isCarriedOver = (lookup: {
  source: NordicLookupSource
}): boolean => lookup.source === 'monitoring'

export const formatDate = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export const formatDateTime = (value: string | null): string => {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
